import {
  extractFileName,
  extractFileType,
  formatCurrentDate,
  formatFileSize,
  formatDate,
  cleanFileName,
} from "./fileUtils";
import { displayUserName } from "./helpers";

/**
 * Data transformation utilities
 * Helper functions to transform API data for UI consumption
 */

/**
 * Transform file details API response to display data for table
 * @param {Object} fileDetailsResponse - Response from /files/details API
 * @param {Array} facilityUserList - Users used to resolve the file creator's name
 * @returns {Array} Array of file display data objects
 */
export const transformFileDetailsToDisplayData = (
  fileDetailsResponse,
  facilityUserList = []
) => {
  if (!fileDetailsResponse || !Array.isArray(fileDetailsResponse.files)) {
    return [];
  }

  return fileDetailsResponse.files.map((file) => ({
    id: file.id,
    name: cleanFileName(file.name),
    description: file.description || "None",
    dateCreated: formatDate(file.created_at),
    // Resolve the actual creator; fall back to "Admin" only when the user
    // isn't in the facility user list (e.g. list not yet loaded).
    createdBy: displayUserName(facilityUserList, file.user_id) || "Admin",
    userId: file.user_id,
    fileType: file.type ? file.type.split("/").pop().toUpperCase() : "UNKNOWN",
    fileSize: formatFileSize(file.size),
    fileUrl: file.blob_url,
    filename: cleanFileName(file.name), // For edit modal
  }));
};

/**
 * Create file relation objects for API submission
 * @param {Array} fileCreationResponse - Response from file creation API
 * @param {string} collectionId - Collection UUID
 * @param {number|null} facilityId - Facility to tag the relation with (superadmin
 *   cross-facility uploads). Ignored unless isSuperAdmin is true.
 * @param {boolean} isSuperAdmin - Whether the caller is a superadmin
 * @returns {Array} File relation objects for API
 */
export const createFileRelationPayload = (
  fileCreationResponse,
  collectionId,
  facilityId = null,
  isSuperAdmin = false
) => {
  if (!Array.isArray(fileCreationResponse) || !collectionId) {
    return [];
  }

  return fileCreationResponse.map((file) => ({
    file_id: file.id,
    file_url: file.blob_url,
    workspace_id: null,
    collection_id: collectionId,
    project_id: null,
    chat_id: null,
    ...(isSuperAdmin && facilityId != null && { facility_id: Number(facilityId) }),
  }));
};

/**
 * Create file relation objects for facility context (relation_type = relation_context)
 * @param {Array} fileCreationResponse - Response from file creation API
 * @param {string} facilityId - Facility UUID
 * @returns {Array} File relation objects for API
 */
export const createFacilityFileRelationPayload = (
  fileCreationResponse,
  facilityId,
  isSuperAdmin = false
) => {
  if (!Array.isArray(fileCreationResponse) || !facilityId) {
    return [];
  }

  return fileCreationResponse.map((file) => ({
    file_id: file.id,
    file_url: file.blob_url,
    relation_type: "facility_context",
    ...(isSuperAdmin && { facility_id: facilityId }),
    workspace_id: null,
    collection_id: null,
    project_id: null,
    chat_id: null,
  }));
};

/**
 * Transform Azure Function response to file creation format
 * @param {Array} azureResponse - Response from Azure Function
 * @param {string} userId - Current user ID
 * @param {number|null} facilityId - Explicit facility number (for superadmin cross-facility uploads)
 * @returns {Array} File creation payload
 */
export const transformAzureResponseToFileData = (azureResponse, userId, facilityId = null) => {
  if (!Array.isArray(azureResponse) || !userId) {
    return [];
  }

  return azureResponse.map((item) => ({
    user_id: userId,
    description: null,
    chat_id: item.chat_id || null,
    message_id: item.message_id || null,
    filename: item.filename,
    blob_url: item.blob_url,
    content_type: item.content_type,
    file_size: item.file_size,
    chunks_count: item.chunks_count,
    project_id: item.project_id || null,
    workspace_id: item.workspace_id || null,
    ...(facilityId != null && { facility_id: facilityId }),
  }));
};
